import angular from "angular";

import { Ajax, Calculations, Configs, Dates, InputEnumOption, Invoice, InvoiceListItem, Invoices, InvoiceStatus, InvoiceType, PaymentChannel, Payrolls, Translations } from "@salaxy/core";

import { InvoicesService, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * Provides read access for authenticated user to access his/her own invoices
 * and write access to create new salary and related payments based on calculations.
 */
export class InvoiceCrudController extends ApiCrudObjectController<Invoice, InvoiceListItem> {
  private _reportCalcs = [];
  private _calcOrPayrollId = [];

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Invoices",
    "UiHelpers",
    "$location",
    "$routeParams",
    "InvoicesService",
    "AjaxNg1",
    "Calculations",
    "Payrolls"
  ];

  /** For list component, defines the layout / functionality of the list. */
  public mode: "default" | "panel" | "person";

  /** Data reader for selected items. */
  public selectionDataReader = {
    /** Function to read data into given array */
    read: (arr: Invoice[], message: string): Promise<void> => { // eslint-disable-line @typescript-eslint/no-unused-vars
      const promises = this.odataController.selectedItems.map(x => this.api.getSingle(x.id));
      return Promise.all(promises).then( (data) => {
        arr.push(...data.filter(d => !!d));
        return;
      });
    },
    /** Optional label for data export */
    exportLabel: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.exportLabel"),
    /** Indicates if the export is not possible */
    disabled: () => !this.odataController.selectedItems || this.odataController.selectedItems.length === 0,
  };

  /** Data reader for selected items. */
  public selectionDataReaderPerson = {
    /** Function to read data into given array */
    read: (arr: Invoice[], message: string): Promise<void> => { // eslint-disable-line @typescript-eslint/no-unused-vars
      const promises = this.odataController.selectedItems.map(x => this.api.getSingle(x.id));
      return Promise.all(promises).then( (data) => {
        data = data.filter(x => x.header.type === InvoiceType.Net || x.header.type === InvoiceType.Tax);
        arr.push(...data.filter(d => !!d));
        return;
      });
    },
    /** Optional label for data export */
    exportLabel: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.exportLabel"),
    /** Indicates if the export is not possible */
    disabled: () => !this.odataController.selectedItems || this.odataController.selectedItems.length === 0,
  };

  /** Status filters for lists: Groups statuses to logical groups. */
  public statusFilters: InputEnumOption[] = [
    {
      value: "all",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusAll"),
      ui: "",
    }, {
      value: "waitingConfirmation",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusWaitingConfirmation"),
      ui: "status eq 'unread' or status eq 'read' or status eq 'waitingConfirmation'",
    }, {
      value: "paid",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusPaid"),
      ui: "status eq 'paid' or status eq 'paymentStarted' or status eq 'canceled'",
    }, {
      value: "forecast",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusForecast"),
      ui: "status eq 'forecast' or status eq 'preview'",
    },
  ];

  constructor(
    protected invoicesApi: Invoices,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private invoicesService: InvoicesService,
    private ajax: Ajax,
  ) { // Dependency injection
    super(invoicesApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Initializes the controller
   */
  public $onInit() {
    super.$onInit();
  }

  /** Gets the default channel for the current account. */
  public get defaultChannel(): PaymentChannel {
    return this.invoicesService.defaultChannel;
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "invoices",
      detailsUrl: this.detailsUrl || "/invoices/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/InvoicesPanel.html",
      oDataOptions: {},
    };
  }

  /** Gets the type of the due date vs. the current date and status for display purposes. */
  public getDueDateType(listItem: InvoiceListItem): "default" | "estimation" | "canceled" | "overdue" | "future"| "unknown" {
    if (listItem.entityType === "unemployment" ) {
      return "estimation";
    }

    switch (listItem.status) {
      case InvoiceStatus.Canceled:
        return "canceled";
      case InvoiceStatus.Forecast:
      case InvoiceStatus.Preview:
        if (listItem.endAt > Dates.getToday()) {
          return "future";
        }
        return "default";
      case InvoiceStatus.Undefined:
      case InvoiceStatus.Unread:
        if (listItem.endAt < Dates.getToday()) {
          return "overdue";
        }
        return "default";
      case InvoiceStatus.Read:
      case InvoiceStatus.WaitingConfirmation:
      case InvoiceStatus.WaitingPalkkaus:
      case InvoiceStatus.Paid:
      case InvoiceStatus.PaymentStarted:
        return "default";
    }
    return "default";
  }

  /** For list components, gets the OData query options. */
  public getListOptions() {
    return this.uiHelpers.cache(this, "listOptions", () => {
      if (this.mode === "panel") {
        return {
          $filter: "",
          $top: 5,
          $orderby: "endAt desc",
        };
      } else {
        // Add logic and e.g. if needed
        return {
          $orderby: "endAt desc",
        };
      }
    });
  }

  /** Returns content url for the invoice. */
  public getContentUrl(id: string, inline = false): string {
    return this.invoicesApi.getContentUrl(id, inline);
  }

  /**
   * Request the status update for the invoice.
   * @param status Status data. If not set, uses the requestedStatus property of the controller.
   */
  public requestStatusUpdate(status: InvoiceStatus = null): any {
    status = status || this.requestedStatus;

    if ( status === InvoiceStatus.Paid && (
      this.current.header.channel === PaymentChannel.Test ||
      this.current.header.channel === PaymentChannel.Procountor ||
      this.current.header.channel === PaymentChannel.VismaNetvisor)) {

      const loader = this.uiHelpers.showLoading("Maksuikkuna avattu...");
      let url = "";
      switch ( this.current.header.channel) {
        case PaymentChannel.Test:
          url =
          Configs.current.isTestData ?
          "https://test-integrations.salaxy.com/test#/dialogs/invoice" :
          "https://integrations.salaxy.com/test#/dialogs/invoice";
          break;
        case PaymentChannel.Procountor:
          url =
          Configs.current.isTestData ?
          "http://localhost:93/procountor/invoice.html" :
          "https://integrations.salaxy.com/procountor/invoice.html";
          break;
        case PaymentChannel.VismaNetvisor:
         url =
         Configs.current.isTestData ?
          "http://localhost:93/vismanetvisor/invoice.html" :
          "https://integrations.salaxy.com/vismanetvisor/invoice.html";
        break;
      }
      url += "?invoiceIds=" + this.currentId + "&token=" + this.ajax.getCurrentToken();
      return this.uiHelpers.showExternalDialog("invoiceCreation", url, {})
        .then(() => {
          loader.dismiss();
          return this.setStatus(this.api.getSingle(this.current.id)).then((invoice) => {
            if (invoice) {
              this.setCurrent(invoice);
            }
            return invoice;
          });
        });
    } else {

      return this.setStatus(this.invoicesApi.requestStatusUpdate(this.currentId, status)
        .then((invoice) => {
          if (invoice) {
            this.setCurrent(invoice);
          }
          return invoice;
        }));
    }
  }

  /** Requested status for binding */
  public get requestedStatus(): InvoiceStatus {
    if (this.current) {
      return (this.current.header as any).requestedStatus || this.current.header.status;
    }
    return null;
  }

  /** Requested status for binding */
  public set requestedStatus(value: InvoiceStatus) {
    (this.current.header as any).requestedStatus = value;
  }

  /**
   * Delete invoice and related business objects (calculations and payrolls)
   * @param item  - Invoice to delete.
   * @param confirmMessage - Optional message for confirm popup
   */
  public deleteWithBusinessObjects(item?: Invoice, confirmMessage?: string): Promise<boolean> {
    item = item || this.current;
    return this.uiHelpers.showConfirm(confirmMessage || "Haluatko varmasti poistaa tämän laskun ja siihen liittyvät laskelmat ja palkkalistat?")
      .then((result: boolean) => {
        if (result) {
          return this.ajax.remove(`/accounts/invoice?ids=${item.id}`).then(() => {
            if (this.current === item) {
              this.setCurrent(null);
            }
            if (this.odataController) {
              this.odataController.reload();
            } else {
              this.$location.url(this.getDefaults().listUrl);
            }
            return true;
          });
        } else {
          return Promise.resolve(false);
        }
      });
  }

  /**
   * Data reader for one item.
   * TODO: Should add interface for the reader.
   */
  public getItemDataReader = (item: {
    /** List item id */
    id: string,
    /** List item owner */
    owner: string,
  }) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => { // eslint-disable-line @typescript-eslint/no-unused-vars
        (item as any).isReading = true;
        return this.api.getSingle(item.id).then((data) => {
          /*
          const wfEvent = {
            type: "PartnerMessageClosed",
            ui: "success" as any,
            message,
          };
          return this.filesApi.saveWorkflowEvent(item, wfEvent).then(() => {
            this.setWorkflowEvent(item as any, wfEvent);
          */
          if (data) {
            arr.push(data);
          }
          (item as any).isReading = false;
          return Promise.resolve();
          /* });*/
        });
      },
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  /** Unselect all */
  public unselectAll() {
    if (this.odataController.selectedItems) {
      this.odataController.selectedItems.splice(0, this.odataController.selectedItems.length);
    }
  }

  /** Select all */
  public selectAll() {
    if (this.odataController.selectedItems && this.odataController.items) {
      this.odataController.selectedItems.splice(0, this.odataController.selectedItems.length);
      this.odataController.selectedItems.push(...this.odataController.items);
    }
  }

  /** Open PaymentDialog for selected row */
  public showPaymentDialog(row: InvoiceListItem) {
    this.invoicesService.showPaymentDialogForCalculation(row);
  }

  /** Get invoices calculation by id */
  public getReportCalcs() {
    const id = this.current.header.businessObjects[0];
    if (!this._calcOrPayrollId.includes(id)) {
      this._calcOrPayrollId.push(id);
      const calculations = new Calculations(this.ajax);
      calculations.getSingle(id).then(calc => {
        if (calc) {
          this._reportCalcs.splice(0,this._reportCalcs.length);
          this._reportCalcs.push(calc);
        } else if(this.current.header.type === InvoiceType.Gross) {
          const payrolls = new Payrolls(this.ajax);
          payrolls.getOData({$filter: `id eq '${this.current.header.businessObjects[0]}'`}).then(result => {
            if(result.items[0] && result.items[0].businessObjects.length) {
              const calculations = new Calculations(this.ajax);
              calculations.getMulti(result.items[0].businessObjects).then(calcs => {
                this._reportCalcs.splice(0,this._reportCalcs.length);
                this._reportCalcs.push(...calcs);
              });
            }
          });
        }
      });
    }
    return this._reportCalcs;
  }
}
